import React, { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { MapPin, ChevronLeft, ChevronRight, Star, X, Users, Moon, Euro } from "lucide-react";

export interface SuggestionFull {
  name: string;
  location: string;
  image_urls: string[];
  price: {
    total_price: number | null;
    currency: string;
    price_per_person?: number | null;
    price_per_night?: number | null;
    from_price?: number | null;
  } | null;
  description: string;
  house_profile?: string;
  equipment_labels?: string[];
  tag_labels?: string[];
  top_naboo?: boolean;
  total_score?: number;
  latitude?: number;
  longitude?: number;
}

interface Props {
  suggestion: SuggestionFull | null;
  onClose: () => void;
}

const formatPrice = (value: number | null | undefined, currency?: string): string => {
  if (value === null || value === undefined) return "";
  const symbol = (!currency || currency === "EUR" || currency === "€") ? "€" : currency;
  return `${Math.round(value).toLocaleString("fr-FR")} ${symbol}`;
};

/** Clean residual HTML tags and fix missing spaces after punctuation */
function cleanText(text: string): string {
  return text
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<p>/gi, "")
    .replace(/<strong>(.*?)<\/strong>/gi, "**$1**")
    .replace(/<em>(.*?)<\/em>/gi, "*$1*")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&nbsp;/g, " ")
    .replace(/\\u0026/g, "&")
    .replace(/([.!?;:,])([A-ZÀ-Ÿa-zà-ÿ])/g, "$1 $2")
    .replace(/ {2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Very simple markdown-to-JSX: headings, bold, lists */
const SimpleMarkdown: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split("\n");
  return (
    <div className="space-y-2 text-sm text-muted-foreground leading-relaxed">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return null;
        if (trimmed.startsWith("### ")) return <h4 key={i} className="font-semibold text-foreground mt-4 mb-1 text-sm">{trimmed.slice(4)}</h4>;
        if (trimmed.startsWith("## ")) return <h3 key={i} className="font-semibold text-foreground mt-4 mb-1 text-base">{trimmed.slice(3)}</h3>;
        if (trimmed.startsWith("# ")) return <h2 key={i} className="font-bold text-foreground mt-4 mb-1 text-lg">{trimmed.slice(2)}</h2>;
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return <li key={i} className="ml-4 list-disc">{renderInline(trimmed.slice(2))}</li>;
        }
        return <p key={i}>{renderInline(trimmed)}</p>;
      })}
    </div>
  );
};

function renderInline(text: string) {
  // Bold: **text**
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i} className="text-foreground font-medium">{part}</strong> : part
  );
}

/** Detect if text is likely English by checking for common English words */
function isLikelyEnglish(text: string): boolean {
  const lower = text.toLowerCase();
  const enWords = [" the ", " and ", " is ", " are ", " with ", " for ", " from ", " this ", " that ", " offers ", " located "];
  const matches = enWords.filter(w => lower.includes(w)).length;
  return matches >= 3;
}

const SuggestionDetailModal: React.FC<Props> = ({ suggestion, onClose }) => {
  const [imageIndex, setImageIndex] = useState(0);

  if (!suggestion) return null;

  const images = suggestion.image_urls?.length ? suggestion.image_urls : ["/placeholder.svg"];
  const currentImage = images[Math.min(imageIndex, images.length - 1)];

  const prevImage = () => setImageIndex((prev) => (prev - 1 + images.length) % images.length);
  const nextImage = () => setImageIndex((prev) => (prev + 1) % images.length);

  const priceTotal = formatPrice(suggestion.price?.total_price, suggestion.price?.currency);
  const pricePP = formatPrice(suggestion.price?.price_per_person, suggestion.price?.currency);
  const priceNight = formatPrice(suggestion.price?.price_per_night, suggestion.price?.currency);

  return (
    <Dialog open onOpenChange={(open) => { if (!open) { setImageIndex(0); onClose(); } }}>
      <DialogContent className="max-w-3xl w-[calc(100%-16px)] sm:w-full p-0 gap-0 max-h-[90vh] overflow-hidden">
        {/* Close button */}
        <button
          onClick={() => { setImageIndex(0); onClose(); }}
          className="absolute top-3 right-3 z-20 rounded-full bg-black/50 p-1.5 text-white hover:bg-black/70 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        <ScrollArea className="max-h-[90vh]">
          {/* Image gallery */}
          <div className="relative w-full aspect-[16/8] bg-muted">
            <img
              src={currentImage}
              alt={suggestion.name}
              className="w-full h-full object-cover"
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white hover:bg-black/60 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white hover:bg-black/60 transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                {/* Dots */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                  {images.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setImageIndex(i)}
                      className={`h-2 w-2 rounded-full transition-colors ${i === imageIndex ? "bg-white" : "bg-white/50"}`}
                    />
                  ))}
                </div>
              </>
            )}
            {/* Price + Top Naboo badges */}
            <div className="absolute top-3 left-3 flex items-center gap-2 z-10">
              <span className="bg-white/90 backdrop-blur-sm text-foreground text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
                {priceTotal || "Prix sur demande"}
              </span>
              {suggestion.top_naboo && (
                <span className="bg-cta-green text-cta-green-foreground text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-sm">
                  🏅 Top Naboo
                </span>
              )}
            </div>
          </div>

          <div className="p-5 sm:p-6 space-y-5">
            {/* Header */}
            <div>
              <h2 className="text-xl sm:text-2xl font-bold">{suggestion.name}</h2>
              {suggestion.location && (
                <div className="flex items-center text-muted-foreground text-sm mt-1">
                  <MapPin className="h-4 w-4 mr-1.5 flex-shrink-0" />
                  <span>{suggestion.location}</span>
                </div>
              )}
            </div>

            {/* Secondary price details */}
            {(pricePP || priceNight) && (
              <div className="flex flex-wrap gap-3">
                {pricePP && (
                  <div className="flex items-center gap-1.5 bg-secondary rounded-xl px-3 py-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    {pricePP} / pers.
                  </div>
                )}
                {priceNight && (
                  <div className="flex items-center gap-1.5 bg-secondary rounded-xl px-3 py-2 text-sm">
                    <Moon className="h-4 w-4 text-muted-foreground" />
                    {priceNight} / nuit
                  </div>
                )}
              </div>
            )}

            {/* Tags */}
            {suggestion.tag_labels && suggestion.tag_labels.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {suggestion.tag_labels.map((tag, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>
                ))}
              </div>
            )}

            {/* Description */}
            {suggestion.description && (
              <div>
                <h3 className="font-semibold text-sm mb-2">Description</h3>
                <SimpleMarkdown content={cleanText(suggestion.description)} />
              </div>
            )}

            {/* House profile (markdown) - only show if it appears to be in French */}
            {suggestion.house_profile && !isLikelyEnglish(suggestion.house_profile) && (
              <div>
                <SimpleMarkdown content={cleanText(suggestion.house_profile)} />
              </div>
            )}

            {/* Equipment */}
            {suggestion.equipment_labels && suggestion.equipment_labels.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-2">Équipements</h3>
                <div className="flex flex-wrap gap-2">
                  {suggestion.equipment_labels.map((eq, i) => (
                    <Badge key={i} variant="secondary" className="text-xs font-normal">{eq}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default SuggestionDetailModal;
